import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sparkles, ArrowRight, Mail, Phone, Globe } from "lucide-react";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [loginMethod, setLoginMethod] = useState("email");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && typeof user === "object") return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    
    if (loginMethod === "email" && password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    
    setSubmitting(true);
    
    let identifier = email.trim().toLowerCase();
    let pass = password;
    let method = loginMethod;
    
    if (loginMethod === "phone") {
      identifier = phone.replace(/\D/g, "");
      pass = phoneCode;
    }
    
    const res = await register(name.trim(), identifier, pass, method);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || "Registration failed");
      return;
    }
    navigate("/");
  };

  const requestPhoneCode = async () => {
    if (!phone || phone.length < 10) {
      setError("Enter valid phone number");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/phone/request-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (data.success) {
        setCodeSent(true);
      } else {
        setError(data.detail || "Failed to send code");
      }
    } catch (e) {
      setError("Network error");
    }
    setSubmitting(false);
  };

  const loginMethods = [
    { id: "email", label: "Email", icon: Mail },
    { id: "phone", label: "Phone", icon: Phone },
    { id: "google", label: "Google", icon: Globe },
  ];

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-slate-50">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-slate opacity-[0.08]" aria-hidden />
        <div className="relative flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-white flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-slate-900" strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-display text-xl font-bold tracking-tight">FullEcosystem</div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60">Unified workspace</div>
          </div>
        </div>

        <div className="relative space-y-10">
          <div>
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/50 mb-4">
              Three apps · one login
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.05]">
              Merge every app you've built into a single, focused workspace.
            </h2>
            <p className="mt-5 text-base text-white/70 leading-relaxed max-w-md">
              App Forge, Calendar-Maps, and Quran Connections now share one
              identity, one shell, and one preview — ready to deploy when you are.
            </p>
          </div>
        </div>

        <div className="relative font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">
          v0.1 · preview build
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-12">
        <div className="w-full max-w-md">
          <div className="mb-10 lg:hidden flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-slate-900 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="font-display text-lg font-bold">FullEcosystem</div>
          </div>

          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-3">
            Create account
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Set up your ecosystem.
          </h1>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            One form. Three apps ready immediately.
          </p>

          {/* Login Method Tabs */}
          <div className="mt-6 flex gap-2">
            {loginMethods.map(({ id, label, icon: Icon }) => (
              <Button
                key={id}
                type="button"
                variant={loginMethod === id ? "default" : "outline"}
                onClick={() => setLoginMethod(id)}
                className="flex-1"
              >
                <Icon className="h-4 w-4 mr-2" />
                {label}
              </Button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs font-medium uppercase tracking-wider text-slate-600">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                required
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-11 bg-slate-50 border-slate-200"
              />
            </div>

            {loginMethod === "email" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wider text-slate-600">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    placeholder="you@domain.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 bg-slate-50 border-slate-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wider text-slate-600">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    minLength={6}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 bg-slate-50 border-slate-200"
                  />
                </div>
              </>
            )}

            {loginMethod === "phone" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-xs font-medium uppercase tracking-wider text-slate-600">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    placeholder="+1 234 567 8900"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="h-11 bg-slate-50 border-slate-200"
                  />
                </div>
                {!codeSent ? (
                  <Button type="button" onClick={requestPhoneCode} className="w-full">
                    Send Code
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="code" className="text-xs font-medium uppercase tracking-wider text-slate-600">
                      SMS Code
                    </Label>
                    <Input
                      id="code"
                      type="text"
                      required
                      placeholder="123456"
                      value={phoneCode}
                      onChange={(e) => setPhoneCode(e.target.value)}
                      className="h-11 bg-slate-50 border-slate-200"
                    />
                  </div>
                )}
              </>
            )}

            {loginMethod === "google" && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Sign up with your Google account.
                </p>
                <Button type="button" className="w-full bg-blue-600 hover:bg-blue-700">
                  <Globe className="h-4 w-4 mr-2" />
                  Continue with Google
                </Button>
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting || (loginMethod === "phone" && !codeSent)}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium"
            >
              {submitting ? "Creating account…" : "Create account"}
              {!submitting && <ArrowRight className="ml-1 h-4 w-4" />}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-200 text-sm text-slate-600">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-slate-900 underline underline-offset-4 hover:text-blue-700">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}